// Tours.jsx (or SriLankaTours.jsx)
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Images ────────────────────────────────────────────────
import toursHeroBg from '../assets/Tours/1.jpg';
import kandeViharaImg from '../assets/Tours/585050.jpg';
import bentotaRiverImg from '../assets/Tours/bentota-river-safari.jpg';
import galleOldCityImg from '../assets/Tours/galle-dutch-fort-sri-lanka-header-1440x1440.jpg';
import sigiriyaImg from '../assets/Tours/Sigiriya-Rock-Sri-Lanka.jpg';
import kandyCulturalImg from '../assets/Tours/chathura-anuradha-subasinghe-40uQmE9Zq8g-unsplash-1024x683.jpg';
import ellaImg from '../assets/Mahi/800x750-4.jpg';

// ────────────────────────────────────────────────
// Variants
// ────────────────────────────────────────────────
const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const cardScale = {
    hidden: { opacity: 0, scale: 0.94, y: 20 },
    visible: (i) => ({
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { delay: i * 0.07, duration: 0.7, ease: 'easeOut' },
    }),
};

const modalVariants = {
    hidden: { opacity: 0, scale: 0.92, y: 30 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
    exit: { opacity: 0, scale: 0.94, y: 20, transition: { duration: 0.3 } },
};

const Tours = () => {
    const { t } = useTranslation('tours');
    const [activeTourId, setActiveTourId] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const tours = [
        { id: 'kande', title: t('kandeTitle'), image: kandeViharaImg, alt: t('kandeBuddhaAlt'), details: [t('kandeDetail1'), t('kandeDetail2'), t('kandeDetail3'), t('kandeDetail4')] },
        { id: 'bentota', title: t('bentotaTitle'), image: bentotaRiverImg, alt: t('bentotaAlt'), details: [t('bentotaDetail1'), t('bentotaDetail2'), t('bentotaDetail3'), t('bentotaDetail4'), t('bentotaDetail5')] },
        { id: 'galle', title: t('galleTitle'), image: galleOldCityImg, alt: t('galleAlt'), details: [t('galleDetail1'), t('galleDetail2'), t('galleDetail3'), t('galleDetail4'), t('galleDetail5')] },
        { id: 'sigiriya', title: t('sigiriyaTitle'), image: sigiriyaImg, alt: t('sigiriyaAlt'), details: [t('sigiriyaDetail1'), t('sigiriyaDetail2'), t('sigiriyaDetail3'), t('sigiriyaDetail4'), t('sigiriyaDetail5')] },
        { id: 'kandy', title: t('kandyTitle'), image: kandyCulturalImg, alt: t('kandyAlt'), details: [t('kandyDetail1'), t('kandyDetail2'), t('kandyDetail3'), t('kandyDetail4'), t('kandyDetail5')] },
        { id: 'ella', title: t('ellaTitle'), image: ellaImg, alt: t('ellaAlt'), details: [t('ellaDetail1'), t('ellaDetail2'), t('ellaDetail3'), t('ellaDetail4'), t('ellaDetail5')] },
    ];

    const activeTour = tours.find(t => t.id === activeTourId);

    return (
        <div className="min-h-screen bg-gradient-to-b from-stone-50 via-[#f8f5ec] to-[#e8e4d6] font-sans">

            {/* ─── HERO ──────────────────────────────────────────────── */}
            <header className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden">
                <img
                    src={toursHeroBg}
                    alt="Sri Lanka Tours Hero"
                    className="absolute inset-0 h-full w-full object-cover brightness-[0.68] scale-[1.03] transition-transform duration-[18s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/40 to-black/55" />

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="relative z-10 text-center px-5 sm:px-8 md:px-12 max-w-5xl"
                >
                    <h1 className="
            text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl
            font-serif font-light tracking-tight text-white
            drop-shadow-[0_5px_18px_rgba(0,0,0,0.75)]
          ">
                        {t('heroTitle')}
                    </h1>

                    <p className="
            mt-5 sm:mt-7 md:mt-9
            text-xl sm:text-2xl md:text-3xl lg:text-4xl
            font-medium text-amber-300/95 tracking-wide
            drop-shadow-md
          ">
                        {t('heroSubtitle')}
                    </p>
                </motion.div>
            </header>

            {/* ─── MAIN CONTENT ──────────────────────────────────────── */}
            <main className="relative z-10 pb-16 md:pb-24 pt-12 md:pt-16">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

                    {/* Intro block */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-120px" }}
                        variants={fadeUp}
                        className="text-center mb-14 md:mb-20"
                    >
                        <div className="inline-flex items-center gap-5 mb-5">
                            <div className="h-px w-14 md:w-20 bg-amber-700/30 flex-1" />
                            <h2 className="
                text-3xl sm:text-4xl md:text-5xl lg:text-6xl
                font-serif font-light text-[#1A2F2A] tracking-tight
              ">
                                {t('heroSubtitle')}
                            </h2>
                            <div className="h-px w-14 md:w-20 bg-amber-700/30 flex-1" />
                        </div>

                        <p className="
              max-w-3xl mx-auto
              text-lg sm:text-xl md:text-2xl
              text-teal-800/85 leading-relaxed font-light
            ">
                            {t('introPara2')}
                        </p>
                    </motion.div>

                    {/* Cards Grid */}
                    <div className="grid gap-6 sm:gap-7 md:gap-8 lg:gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {tours.map((tour, idx) => (
                            <motion.button
                                key={tour.id}
                                custom={idx}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardScale}
                                onClick={() => setActiveTourId(tour.id)}
                                className="
                  group relative overflow-hidden rounded-2xl md:rounded-3xl
                  bg-white shadow-xl hover:shadow-2xl
                  transition-all duration-500 ease-out
                  border border-teal-100/70 hover:border-amber-200/60
                  flex flex-col h-full
                "
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={tour.image}
                                        alt={tour.alt}
                                        className="
                      w-full h-full object-cover
                      transition-transform duration-900 ease-out
                      group-hover:scale-110
                    "
                                        loading="lazy"
                                    />
                                    <div className="
                    absolute inset-0 bg-gradient-to-t
                    from-black/60 via-black/25 to-transparent
                    opacity-70 group-hover:opacity-85
                    transition-opacity duration-600
                  " />
                                </div>

                                <div className="p-6 md:p-7 flex flex-col flex-grow">
                                    <h3 className="
                    text-2xl md:text-3xl font-light
                    text-[#1A2F2A] group-hover:text-[#2D3F3A]
                    transition-colors duration-400
                  ">
                                        {tour.title}
                                    </h3>

                                    <p className="
                    mt-3 text-base md:text-lg text-gray-700
                    leading-relaxed line-clamp-3
                  ">
                                        {tour.details[0]}
                                    </p>

                                    <div className="mt-auto pt-6">
                                        <span className="
                      inline-flex items-center gap-2
                      text-[#E8C07D] font-medium text-lg
                      group-hover:text-[#D4A860] transition-colors
                    ">
                                            {t('viewDetails') || 'View Details'}
                                            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>

                </div>
            </main>

            {/* ─── MODAL ─────────────────────────────────────────────── */}
            <AnimatePresence>
                {activeTour && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                        onClick={() => setActiveTourId(null)}
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="
                relative bg-gradient-to-b from-[#fdfaf2] to-[#f5f1e6]
                rounded-2xl md:rounded-3xl shadow-2xl
                w-full max-w-4xl max-h-[92vh] overflow-y-auto
                border border-teal-100/60
              "
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setActiveTourId(null)}
                                className="
                  absolute top-4 right-4 md:top-6 md:right-6
                  w-10 h-10 md:w-12 md:h-12 rounded-full
                  bg-white/90 hover:bg-white text-teal-900
                  flex items-center justify-center text-2xl md:text-3xl
                  shadow hover:shadow-lg transition-all z-10
                "
                                aria-label="Close"
                            >
                                ×
                            </button>

                            <div className="p-6 md:p-10 lg:p-12">
                                {/* Header + Image */}
                                <div className="mb-8 md:mb-10">
                                    <h2 className="
                    text-3xl md:text-4xl lg:text-5xl
                    font-serif font-light text-[#1A2F2A] mb-4 md:mb-6
                  ">
                                        {activeTour.title}
                                    </h2>

                                    <div className="rounded-2xl overflow-hidden shadow-lg border border-teal-50">
                                        <img
                                            src={activeTour.image}
                                            alt={activeTour.alt}
                                            className="w-full h-64 md:h-80 lg:h-96 object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="prose prose-teal max-w-none text-gray-800 text-[15.5px] md:text-base lg:text-lg leading-relaxed">
                                    {activeTour.details.map((para, i) => (
                                        <p key={i} className="mb-5 md:mb-6 last:mb-0">{para}</p>
                                    ))}
                                </div>

                                {/* CTA */}
                                <div className="mt-10 md:mt-14 text-center">
                                    <a
                                        href="https://wa.me/+94766111678"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="
                      inline-flex items-center gap-3
                      bg-gradient-to-r from-amber-500 to-amber-600
                      hover:from-amber-600 hover:to-amber-700
                      text-white font-medium text-lg md:text-xl
                      px-10 py-5 rounded-full shadow-xl
                      hover:shadow-2xl transform hover:-translate-y-1
                      transition-all duration-400
                    "
                                    >
                                        <span>{t('bookTour') || 'Book This Tour Now'}</span>
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Tours;